// API client para la carta Gantt de ODT (planificación en dos niveles).
// Consume los endpoints de hallazgoFlujo.routes.ts:
//   PATCH  /orders/:id                 -> cabecera (fecha comprometida / apagar Gantt)
//   POST   /orders/:id/gantt           -> encender la carta Gantt + reparto automático
//   POST   /orders/:id/dependencias    -> crear dependencia fin->inicio entre tareas
//   DELETE /orders/:id/dependencias    -> borrar dependencia
//   POST   /orders/:id/items          -> crear una tarea de la ODT
//
// El summary (GET /orders/:id/summary) ya vive en src/api/hallazgoFlujo.js
// (getOrderSummaryApi) y no se duplica acá.
//
// Sigue el patrón de src/api/hallazgoFlujo.js: fetch + getAccessTokenApi +
// manejo de errores homogéneo.
import { getAccessTokenApi } from './auth';
import { BASE_PATH_SERVER } from './config';

const jsonHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: getAccessTokenApi(),
});

const handleError = (err) => ({
  status: 'client',
  codeMessage: 'ERROR_EN_LA_PETICION',
  message: err?.message ?? 'Error desconocido',
});

const parseResponse = async (res) => {
  const data = await res.json();
  if (!res.ok) {
    return {
      codeMessage: 'HTTP_ERROR',
      message: data?.message ?? `Error ${res.status}`,
      ...data,
    };
  }
  return data;
};

/**
 * Edita la cabecera de la ODT sin encender la carta Gantt: fecha de término
 * comprometida y/o apagado del modo (modo_gantt: false). Encender NO se hace por
 * acá (el backend lo rechaza con 400) — se usa encenderGanttApi.
 * @param {number} idOrder
 * @param {{fecha_comprometida?: string|null, modo_gantt?: false}} payload
 * @returns {Promise<{id:number, fecha_comprometida:string|null, modo_gantt:boolean}>}
 */
export const patchOrderHeaderApi = async (idOrder, payload) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}orders/${idOrder}`, {
      method: 'PATCH',
      headers: jsonHeaders(),
      body: JSON.stringify(payload),
    });
    return await parseResponse(res);
  } catch (err) {
    return handleError(err);
  }
};

/**
 * Enciende la carta Gantt de la ODT: setea modo_gantt=true y reparte fechas
 * automáticamente en las tareas que no las tengan (idempotente). Si se pasa
 * fecha_comprometida, la fija de paso.
 * @param {number} idOrder
 * @param {{fecha_comprometida?: string}} payload
 * @returns {Promise<{id:number, modo_gantt:boolean, fecha_comprometida:string|null, tareas_repartidas:number, dependencias_creadas:number}>}
 */
export const encenderGanttApi = async (idOrder, payload = {}) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}orders/${idOrder}/gantt`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(payload),
    });
    return await parseResponse(res);
  } catch (err) {
    return handleError(err);
  }
};

/**
 * Edita la planificación de UNA tarea (fechas, responsable, jerarquía, orden) sin
 * tocar su contenido ni reenviar el resto. Update parcial: un campo ausente queda
 * intacto, un null explícito borra.
 * @param {number} idOrder
 * @param {number} idItem
 * @param {{planned_start?: string|null, planned_end?: string|null, parent_id?: number|null, sort_order?: number, assigned_to_user_id?: number|null}} campos
 * @returns {Promise<{id:number, planned_start:string|null, planned_end:string|null, parent_id:number|null, sort_order:number, assigned_to_user_id:number|null}>}
 */
export const patchOrderItemApi = async (idOrder, idItem, campos) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}orders/${idOrder}/items/${idItem}`, {
      method: 'PATCH',
      headers: jsonHeaders(),
      body: JSON.stringify(campos),
    });
    return await parseResponse(res);
  } catch (err) {
    return handleError(err);
  }
};

/**
 * Crea una dependencia fin->inicio entre dos tareas de la misma ODT.
 * @param {number} idOrder
 * @param {{id_item:number, id_item_predecesor:number}} payload
 * @returns {Promise<{creada:boolean}>}
 */
export const addDependenciaApi = async (idOrder, { id_item, id_item_predecesor }) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}orders/${idOrder}/dependencias`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ id_item, id_item_predecesor }),
    });
    return await parseResponse(res);
  } catch (err) {
    return handleError(err);
  }
};

/**
 * Borra una dependencia entre dos tareas de la ODT.
 * @param {number} idOrder
 * @param {{id_item:number, id_item_predecesor:number}} payload
 * @returns {Promise<{borradas:number}>}
 */
export const removeDependenciaApi = async (idOrder, { id_item, id_item_predecesor }) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}orders/${idOrder}/dependencias`, {
      method: 'DELETE',
      headers: jsonHeaders(),
      body: JSON.stringify({ id_item, id_item_predecesor }),
    });
    return await parseResponse(res);
  } catch (err) {
    return handleError(err);
  }
};

/**
 * Crea UNA tarea de la ODT desde la carta Gantt. Con `parent_id` nace como
 * subtarea; sin él, como tarea de primer nivel al final de la lista.
 * @param {number} idOrder
 * @param {{description: string, parent_id?: number|null, planned_start?: string|null,
 *          planned_end?: string|null, assigned_to_user_id?: number|null,
 *          id_ascensor?: number|null, id_coleccion_item?: number|null,
 *          cantidad?: number|null}} payload
 * @returns {Promise<{id:number, description:string, parent_id:number|null, sort_order:number,
 *          planned_start:string|null, planned_end:string|null, assigned_to_user_id:number|null}>}
 */
export const crearItemApi = async (idOrder, payload) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}orders/${idOrder}/items`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(payload),
    });
    return await parseResponse(res);
  } catch (err) {
    return handleError(err);
  }
};
